import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, CalendarDays, Check, ChevronRight, Copy, ExternalLink, MapPin, Phone, RefreshCw, X } from 'lucide-react';
import { apiClient } from '../lib/api';
import { schoolDate, schoolHomepage, schoolValue, type SchoolClasses, type SchoolOverviewInfo } from '../lib/schoolOverview';
import { useSchoolDisclosure } from '../hooks/useSchoolDisclosure';
import { SchoolDisclosureDetails, SchoolDisclosureSummary } from './SchoolDisclosurePanel';

export function SchoolOverviewCard({ school }: { school: SchoolOverviewInfo }) {
  const value = (key: string) => schoolValue(school, key);
  const name = value('SCHUL_NM') || '우리 학교';
  const officeCode = value('ATPT_OFCDC_SC_CODE');
  const schoolCode = value('SD_SCHUL_CODE');
  const schoolKey = `${officeCode}:${schoolCode}`;
  const disclosure = useSchoolDisclosure(officeCode, schoolCode);
  const homepage = schoolHomepage(value('HMPG_ADRES'));
  const address = [value('ORG_RDNMA'), value('ORG_RDNDA')].filter(Boolean).join(' ');
  const phone = value('ORG_TELNO');
  const mapUrl = `https://map.naver.com/p/search/${encodeURIComponent(address || name)}`;
  const [result, setResult] = useState<{ key: string; data: SchoolClasses | null; error: boolean } | null>(null);
  const [retry, setRetry] = useState(0);
  const [open, setOpen] = useState(false);
  const [copyStatus, setCopyStatus] = useState('');
  const dialogRef = useRef<HTMLDialogElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const detailTargetRef = useRef<'disclosure' | 'classes'>('disclosure');
  const currentResult = result?.key === schoolKey ? result : null;
  const classes = currentResult?.data ?? null;
  const loading = Boolean(officeCode && schoolCode && !currentResult);
  const hasClasses = Boolean(classes?.groups.length);

  useEffect(() => {
    if (!officeCode || !schoolCode) return;
    const controller = new AbortController();
    setResult(null);
    apiClient.getSchoolClasses(officeCode, schoolCode, controller.signal)
      .then((data) => { if (!controller.signal.aborted) setResult({ key: schoolKey, data, error: false }); })
      .catch(() => { if (!controller.signal.aborted) setResult({ key: schoolKey, data: null, error: true }); });
    return () => controller.abort();
  }, [officeCode, schoolCode, schoolKey, retry]);

  useEffect(() => {
    if (!open || !dialogRef.current) return;
    const dialog = dialogRef.current;
    const previousOverflow = document.body.style.overflow;
    dialog.showModal();
    if (detailTargetRef.current === 'classes') {
      const body = dialog.querySelector<HTMLElement>('.fe-school-dialog-body');
      const target = dialog.querySelector<HTMLElement>('#school-classes-title');
      if (body && target) body.scrollTop += target.getBoundingClientRect().top - body.getBoundingClientRect().top - 16;
    }
    document.body.style.overflow = 'hidden';
    return () => {
      dialog.close();
      document.body.style.overflow = previousOverflow;
      if (triggerRef.current?.isConnected) triggerRef.current.focus();
    };
  }, [open]);

  useEffect(() => { setOpen(false); setCopyStatus(''); }, [schoolKey]);

  const facts = [
    ['학교급', value('SCHUL_KND_SC_NM')],
    ['설립 구분', value('FOND_SC_NM')],
    ['남녀 구분', value('COEDU_SC_NM')],
    ['주야 구분', value('DGHT_SC_NM')],
    ['학교 유형', value('HS_SC_NM')],
    ['계열', value('HS_GNRL_BUSNS_SC_NM')],
    ['관할 교육청', value('ATPT_OFCDC_SC_NM')],
    ['교육지원청', value('JU_ORG_NM')],
    ['설립일', schoolDate(value('FOND_YMD'))],
    ['개교기념일', schoolDate(value('FOAS_MEMRD'), true)],
  ].filter(([, text]) => text);
  const badges = [value('SCHUL_KND_SC_NM'), value('FOND_SC_NM'), value('COEDU_SC_NM')].filter(Boolean);

  async function copyAddress() {
    try { await navigator.clipboard.writeText(address); setCopyStatus('주소를 복사했어요.'); }
    catch { setCopyStatus('복사하지 못했어요. 주소를 선택해 복사해 주세요.'); }
  }

  function showDetails(target: 'disclosure' | 'classes' = 'disclosure') {
    detailTargetRef.current = target;
    triggerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setCopyStatus('');
    setOpen(true);
  }

  const classMessage = loading ? '학급 정보를 불러오는 중이에요.'
    : currentResult?.error ? '학급 정보를 불러오지 못했어요.'
    : '공개된 학급 정보가 아직 없어요.';

  return (
    <div className="fe-school-overview fe-surface rounded-3xl border p-4 sm:p-5 h-full flex flex-col">
      <div className="flex items-center gap-3 mb-3">
        <div className="fe-home-icon fe-home-icon--sage"><Building2 className="w-5 h-5" aria-hidden="true" /></div>
        <div className="min-w-0">
          <p className="text-[11px] text-[#748069] mb-0.5">우리 학교</p>
          <h3 className="font-bold break-words">{name}</h3>
        </div>
      </div>
      {badges.length > 0 && <div className="flex flex-wrap gap-1.5 mb-4">{badges.map((badge) => <span className="fe-school-badge" key={badge}>{badge}</span>)}</div>}

      <SchoolDisclosureSummary state={disclosure} onDetails={() => showDetails()} />

      {hasClasses && classes ? (
        <button type="button" className="fe-school-stats" onClick={() => showDetails('classes')} aria-haspopup="dialog" aria-label="학년별 학급 구성 자세히 보기">
          <span className="flex items-center justify-between text-xs text-[#65745a] mb-3"><span>{classes.academicYear}학년도 학급 구성</span><ChevronRight size={15} aria-hidden="true" /></span>
          <span className="grid grid-cols-2 gap-3 text-left">
            <span><span className="block text-xs text-[#6b7562] mb-1">전체 학급</span><strong className="text-2xl tracking-tight">{classes.totalClasses.toLocaleString('ko-KR')}</strong><span className="text-xs ml-1">학급</span></span>
            <span className="border-l border-[#d7decc] pl-4"><span className="block text-xs text-[#6b7562] mb-1">운영 학년</span><strong className="text-2xl tracking-tight">{classes.gradeCount}</strong><span className="text-xs ml-1">개</span></span>
          </span>
        </button>
      ) : (
        <div className="fe-school-stats text-xs text-[#6b7562]" role="status">
          <p>{classMessage}</p>
          {currentResult?.error && <button type="button" className="fe-school-retry" onClick={() => setRetry((count) => count + 1)}><RefreshCw size={13} aria-hidden="true" />다시 불러오기</button>}
        </div>
      )}
      <div className="space-y-3 my-4 text-xs text-[#626b5c]">
        {phone && <a href={`tel:${phone.replace(/[^\d+]/g, '')}`} className="flex items-center gap-2 w-fit min-h-8 hover:underline"><Phone size={14} className="shrink-0" aria-hidden="true" /><span>{phone}</span></a>}
        {address && <a href={mapUrl} target="_blank" rel="noopener noreferrer" className="flex items-start gap-2 hover:underline"><MapPin size={14} className="shrink-0 mt-0.5" aria-hidden="true" /><span className="break-words">{address}</span><ExternalLink size={11} className="shrink-0 mt-0.5" aria-hidden="true" /></a>}
      </div>
      <div className="mt-auto">
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-[#637151]">
          <Link to="/workmate/timetable" className="fe-school-quick-link"><CalendarDays size={13} aria-hidden="true" />시간표</Link>
          {homepage && <a href={homepage} target="_blank" rel="noopener noreferrer" className="fe-school-quick-link">학교 홈페이지<ExternalLink size={12} aria-hidden="true" /></a>}
        </div>
        <p className="text-[10px] text-[#85907c] mt-1">출처: NEIS · 학교알리미</p>
      </div>

      {open && (
        <dialog ref={dialogRef} className="fe-school-dialog" aria-labelledby="school-overview-title" onCancel={() => setOpen(false)} onClose={() => setOpen(false)} onKeyDown={(event) => {
          if (event.key !== 'Tab') return;
          const elements = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], summary, [tabindex="0"]')).filter((element) => element.checkVisibility());
          const first = elements[0];
          const last = elements[elements.length - 1];
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
        }} onClick={(event) => {
          if (event.target !== event.currentTarget) return;
          const rect = event.currentTarget.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) setOpen(false);
        }}>
          <div className="fe-school-dialog-heading">
            <div className="min-w-0"><p className="text-xs text-[#78826e] mb-1">우리 학교 자세히 보기</p><h2 id="school-overview-title" className="text-xl font-bold break-words">{name}</h2></div>
            <button type="button" className="fe-school-dialog-close" aria-label="학교정보 닫기" onClick={() => setOpen(false)} autoFocus><X size={20} /></button>
          </div>
          <div className="fe-school-dialog-body">
            <SchoolDisclosureDetails state={disclosure} />
            <section aria-labelledby="school-classes-title">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3"><h3 id="school-classes-title" className="font-semibold">학년별 학급 구성</h3>{classes && <span className="fe-school-badge">{classes.academicYear}학년도</span>}</div>
              {hasClasses && classes ? <>
                <p className="text-sm text-[#66735a] mb-3">공개된 학급은 총 <strong>{classes.totalClasses}학급</strong>입니다.</p>
                <div className="fe-school-class-list">{classes.groups.map((group) => (
                  <div key={JSON.stringify([group.course, group.grade, group.department, group.dayNight, group.track])} className="fe-school-class-row">
                    <div className="flex items-center justify-between gap-3"><div><h4 className="text-sm font-semibold">{group.grade}학년</h4><p className="text-[11px] text-[#7b8572] mt-1">{[group.course, group.department && group.department !== '일반학과' ? group.department : '', group.dayNight === '야간' ? '야간' : ''].filter(Boolean).join(' · ')}</p></div><span className="text-sm font-semibold whitespace-nowrap">{group.classes.length}학급</span></div>
                    <div className="flex flex-wrap gap-1.5 mt-2">{group.classes.map((className) => <span key={className} className="fe-school-class-chip">{/^\d+$/.test(className) ? `${className}반` : className}</span>)}</div>
                  </div>
                ))}</div>
                <p className="fe-school-source">NEIS 학급정보{classes.updatedAt ? ` · 자료 갱신 ${classes.updatedAt}` : ''}<br />공개된 학급 편성 기준이며, 실제 운영 현황과 다를 수 있습니다.</p>
              </> : <div className="fe-school-empty" role="status"><p>{classMessage}</p>{currentResult?.error && <button type="button" className="fe-school-retry" onClick={() => setRetry((count) => count + 1)}><RefreshCw size={13} aria-hidden="true" />다시 불러오기</button>}</div>}
            </section>

            <section aria-labelledby="school-basic-title"><h3 id="school-basic-title" className="font-semibold mb-3">학교 기본정보</h3>
              {facts.length ? <dl className="fe-school-facts">{facts.map(([label, text]) => <div key={label}><dt>{label}</dt><dd>{text}</dd></div>)}</dl> : <p className="text-sm text-[#78826e]">추가로 공개된 기본정보가 없습니다.</p>}
              {schoolDate(value('LOAD_DTM').slice(0, 8)) && <p className="fe-school-source">기본정보 갱신 {schoolDate(value('LOAD_DTM').slice(0, 8))}</p>}
            </section>

            <section aria-labelledby="school-contact-title"><h3 id="school-contact-title" className="font-semibold mb-3">연락처와 찾아오는 길</h3>
              <dl className="fe-school-facts">
                {disclosure.data?.contacts?.staffRoom && <div><dt>교무실</dt><dd><a className="underline underline-offset-4" href={`tel:${disclosure.data.contacts.staffRoom.replace(/[^\d+]/g, '')}`}>{disclosure.data.contacts.staffRoom}</a></dd></div>}
                {disclosure.data?.contacts?.administration && <div><dt>행정실</dt><dd><a className="underline underline-offset-4" href={`tel:${disclosure.data.contacts.administration.replace(/[^\d+]/g, '')}`}>{disclosure.data.contacts.administration}</a></dd></div>}
                {phone && <div><dt>대표 전화</dt><dd><a className="underline underline-offset-4" href={`tel:${phone.replace(/[^\d+]/g, '')}`}>{phone}</a></dd></div>}
                {value('ORG_FAXNO') && <div><dt>팩스</dt><dd>{value('ORG_FAXNO')}</dd></div>}
                {address && <div><dt>주소</dt><dd>{value('ORG_RDNZC') && <span className="block text-xs text-[#78826e] mb-1">우편번호 {value('ORG_RDNZC')}</span>}{address}</dd></div>}
              </dl>
              {!phone && !address && !value('ORG_FAXNO') && <p className="text-sm text-[#78826e]">공개된 연락처와 주소가 없습니다.</p>}
              <div className="flex flex-wrap gap-2 mt-3">
                {address && <button type="button" className="fe-school-link-button" onClick={copyAddress}>{copyStatus === '주소를 복사했어요.' ? <Check size={14} /> : <Copy size={14} />}주소 복사</button>}
                {address && <a className="fe-school-link-button" href={mapUrl} target="_blank" rel="noopener noreferrer"><MapPin size={14} />지도 보기<ExternalLink size={12} /></a>}
                {homepage && <a className="fe-school-link-button" href={homepage} target="_blank" rel="noopener noreferrer">학교 홈페이지<ExternalLink size={12} /></a>}
              </div>
              <p className="text-xs text-[#637151] mt-2" role="status">{copyStatus}</p>
            </section>

            <aside className="fe-school-disclosure"><h3 className="font-semibold text-sm mb-2">학교의 공시자료 더 보기</h3><p className="text-xs leading-relaxed text-[#6c7464] mb-3">예·결산과 운영계획 등 더 자세한 자료는 학교알리미 원문에서 확인할 수 있어요.</p><a className="fe-school-link-button" href={disclosure.data?.sourceUrl || 'https://www.schoolinfo.go.kr/'} target="_blank" rel="noopener noreferrer">학교알리미 열기<ExternalLink size={12} /></a></aside>
            <p className="fe-school-source">출처: NEIS 교육정보 개방 포털 · 학교별 공개 범위에 따라 표시되는 정보가 다릅니다.</p>
          </div>
        </dialog>
      )}
    </div>
  );
}
